import { ComponentType, SVGProps, createContext, useContext, useEffect } from 'react';

// ----------------------------------------------------------------------
// Dev-only. Lets a prototype replace the sandbox sidebar with its own
// navigation, so a multi-page feature can be reviewed the way it will sit in
// the real app. Pages are switched via the `?page=` search param.
//
//   const NAV: ProtoNav = { defaultPage: 'org', groups: [...] };   // module const
//   useProtoNav(NAV);                                               // in the prototype
// ----------------------------------------------------------------------

export type ProtoNavItem = {
  title: string;
  icon: ComponentType<SVGProps<SVGElement>>;
  /** Pages (`?page=` values) that highlight this item; the first one is opened on click. */
  pages?: string[];
  /** Item exists in the real nav but is not part of this prototype — rendered muted. */
  muted?: boolean;
  /** Sub-items in the real nav (collapsed there too) — shown as a chevron, listed in the tooltip. */
  children?: string[];
  /** Sub-items shown expanded under the item, as the real nav does for the open section. */
  items?: ProtoNavSubItem[];
};

export type ProtoNavSubItem = Omit<ProtoNavItem, 'icon' | 'items'>;

export type ProtoNavGroup = { subheader: string; items: ProtoNavItem[] };

export type ProtoNav = { defaultPage: string; groups: ProtoNavGroup[] };

type Ctx = { setNav: (nav: ProtoNav | null) => void };

export const ProtoNavContext = createContext<Ctx | null>(null);

export function useProtoNav(nav: ProtoNav) {
  const ctx = useContext(ProtoNavContext);
  useEffect(() => {
    ctx?.setNav(nav);
    return () => ctx?.setNav(null);
  }, [ctx, nav]);
}
